import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react';
import { Post } from '../../types';
import { PostOptionsModal } from './PostOptionsModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { CommentsModal } from './CommentsModal';
import { MediaCarousel } from './MediaCarousel';
import { HashtagLink } from '../hashtag/HashtagLink';
import { useAuthStore } from '../../store/authStore';
import { deletePost } from '../../lib/postService';
import { likePost, unlikePost, savePost, unsavePost } from '../../lib/engagementService';
import { realtimeEngagementService } from '../../lib/realtimeEngagementService';

interface PostCardProps {
  post: Post;
  onPostDeleted?: (postId: string) => void;
}

export const PostCard: React.FC<PostCardProps> = ({ post, onPostDeleted }) => {
  const { user: currentUser } = useAuthStore();
  const { user, caption, location, created_at, updated_at, media, hashtags, like_count, comment_count, is_liked, is_saved } = post;
  
  // Handle both new database posts and legacy mock posts
  const imageUrl = media && media.length > 0 ? media[0].media_url : (post as any).imageUrl;
  const likeCount = like_count !== undefined ? like_count : (post as any).likeCount || 0;
  const commentCount = comment_count !== undefined ? comment_count : (post as any).commentCount || 0;
  const timestamp = created_at ? new Date(created_at).toLocaleDateString() : (post as any).timestamp || 'now';
  const isLiked = is_liked !== undefined ? is_liked : (post as any).isLiked || false;
  const isSaved = is_saved !== undefined ? is_saved : (post as any).isSaved || false;

  const [liked, setLiked] = useState(isLiked);
  const [likes, setLikes] = useState(likeCount);
  const [saved, setSaved] = useState(isSaved);
  const [comments, setComments] = useState(commentCount);
  const [showComments, setShowComments] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isOwnPost = currentUser?.id === post.user_id || currentUser?.id === user.id;

  useEffect(() => {
    setLiked(isLiked);
    setLikes(likeCount);
    setSaved(isSaved);
    setComments(commentCount);
  }, [isLiked, likeCount, isSaved, commentCount]);

  // Subscribe to real-time engagement updates for this post
  useEffect(() => {
    if (!currentUser) return;

    console.log(`🔄 Setting up real-time engagement for post: ${post.id}`);

    const unsubscribe = realtimeEngagementService.subscribeToPostEngagement(
      post.id,
      handleLikeUpdate,
      handleCommentUpdate,
      handleSaveUpdate
    );

    return () => {
      console.log(`🔌 Cleaning up engagement subscription for post: ${post.id}`);
      unsubscribe();
    };
  }, [post.id, currentUser]);

  const handleLikeUpdate = (payload: any) => {
    console.log('❤️ Handling like update:', payload);
    
    if (payload.type === 'added' || payload.type === 'removed') {
      // Real-time update from another user
      if (payload.user_id !== currentUser?.id) {
        setLikes(payload.new_count);
        console.log(`✅ Updated like count to: ${payload.new_count}`);
      }
    } else if (payload.type === 'count_changed') {
      // Polling detected a change
      setLikes(payload.new_count);
      console.log(`📊 Polling updated like count to: ${payload.new_count}`);
    }
  };

  const handleCommentUpdate = (payload: any) => {
    console.log('💬 Handling comment update:', payload);
    
    if (payload.type === 'added' || payload.type === 'removed') {
      // Real-time update from another user
      if (payload.user_id !== currentUser?.id) {
        setComments(payload.new_count);
        console.log(`✅ Updated comment count to: ${payload.new_count}`);
      }
    } else if (payload.type === 'count_changed') {
      // Polling detected a change
      setComments(payload.new_count);
      console.log(`📊 Polling updated comment count to: ${payload.new_count}`);
    }
  };

  const handleSaveUpdate = (payload: any) => {
    console.log('🔖 Handling save update:', payload);
    // Save updates are typically only relevant to the user who saved/unsaved
    // so we don't need to update the UI for other users
  };

  const handleLike = async () => {
    if (!currentUser || isLiking) return;

    setIsLiking(true);
    const newLiked = !liked;
    const newLikes = newLiked ? likes + 1 : likes - 1;

    // Optimistic update
    setLiked(newLiked);
    setLikes(newLikes);

    try {
      if (newLiked) {
        await likePost(post.id, currentUser.id);
      } else {
        await unlikePost(post.id, currentUser.id);
      }

      // Broadcast the like action to other users
      await realtimeEngagementService.broadcastLikeAction(
        post.id,
        currentUser.id,
        newLiked ? 'added' : 'removed',
        newLikes
      );

      // Also broadcast to the general feed
      await realtimeEngagementService.broadcastToFeed(
        post.id,
        'like',
        newLiked ? 'added' : 'removed',
        newLikes
      );

      console.log(`✅ Like ${newLiked ? 'added' : 'removed'} and broadcasted`);
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update
      setLiked(!newLiked);
      setLikes(newLiked ? newLikes - 1 : newLikes + 1);
    } finally {
      setIsLiking(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser || isSaving) return;

    setIsSaving(true);
    const newSaved = !saved;

    // Optimistic update
    setSaved(newSaved);

    try {
      if (newSaved) {
        await savePost(post.id, currentUser.id);
      } else {
        await unsavePost(post.id, currentUser.id);
      }

      // Broadcast the save action
      await realtimeEngagementService.broadcastSaveAction(
        post.id,
        currentUser.id,
        newSaved ? 'added' : 'removed'
      );

      console.log(`✅ Save ${newSaved ? 'added' : 'removed'} and broadcasted`);
    } catch (error) {
      console.error('Error toggling save:', error);
      // Revert optimistic update
      setSaved(!newSaved);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePost = async () => {
    if (!currentUser) return;

    setIsDeleting(true);
    try {
      await deletePost(post.id, currentUser.id);
      setShowDeleteModal(false);
      setShowOptionsModal(false);
      
      // Notify parent component that post was deleted
      if (onPostDeleted) {
        onPostDeleted(post.id);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: `${user.username}'s post`,
      text: caption,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // Handle share cancellation or permission denied gracefully
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled the share, do nothing
          return;
        }
        
        // For other errors (like NotAllowedError), fall back to copying link
        console.log('Share failed, falling back to clipboard:', error);
        try {
          await navigator.clipboard.writeText(window.location.href);
          alert('Link copied to clipboard!');
        } catch (clipboardError) {
          console.error('Clipboard fallback failed:', clipboardError);
          alert('Unable to share or copy link');
        }
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch (error) {
        console.error('Clipboard fallback failed:', error);
        alert('Unable to copy link to clipboard');
      }
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
    alert('Link copied to clipboard!');
  };

  const handleReport = () => {
    alert('Report functionality would be implemented here');
  };

  const handleCommentAdded = async () => {
    const newCommentCount = comments + 1;
    setComments(newCommentCount);

    // Broadcast the comment action
    if (currentUser) {
      await realtimeEngagementService.broadcastCommentAction(
        post.id,
        currentUser.id,
        'added',
        newCommentCount
      );

      await realtimeEngagementService.broadcastToFeed(
        post.id,
        'comment',
        'added',
        newCommentCount
      );
    }
  };

  const handleCommentDeleted = async () => {
    const newCommentCount = Math.max(0, comments - 1);
    setComments(newCommentCount);

    // Broadcast the comment action
    if (currentUser) {
      await realtimeEngagementService.broadcastCommentAction(
        post.id,
        currentUser.id,
        'removed',
        newCommentCount
      );

      await realtimeEngagementService.broadcastToFeed(
        post.id,
        'comment',
        'removed',
        newCommentCount
      );
    }
  };

  const handleMediaChange = (index: number) => {
    setCurrentMediaIndex(index);
  };

  // Prepare media for carousel - handle both new and legacy posts
  const carouselMedia = media && media.length > 0 
    ? media 
    : imageUrl 
      ? [{ 
          id: 'legacy', 
          media_url: imageUrl, 
          media_type: 'image' as const, 
          order_index: 0 
        }] 
      : [];

  // Parse caption for hashtags and create clickable links
  const renderCaption = (text: string) => {
    const parts = text.split(/(#\w+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('#')) {
        return (
          <HashtagLink 
            key={index} 
            hashtag={part} 
            showIcon={false}
            className="text-blue-600 hover:text-blue-700 font-medium"
          />
        );
      }
      return part;
    });
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-md mb-6 overflow-hidden">
        {/* Post Header */}
        <div className="flex items-center justify-between p-3">
          <Link to={`/profile/${user.username}`} className="flex items-center">
            <img 
              src={user.profileImage} 
              alt={user.username} 
              className="h-8 w-8 rounded-full object-cover"
            />
            <div className="ml-3">
              <p className="font-medium text-sm">{user.username}</p>
              {location && <p className="text-xs text-gray-500">{location}</p>}
            </div>
          </Link>
          <button 
            onClick={() => setShowOptionsModal(true)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </div>
        
        {/* Post Media Carousel */}
        <div className="relative">
          {carouselMedia.length > 0 ? (
            <MediaCarousel
              media={carouselMedia}
              className="aspect-square"
              showControls={true}
              onMediaChange={handleMediaChange}
            />
          ) : (
            <div className="aspect-square bg-gray-100 flex items-center justify-center">
              <p className="text-gray-400">No media available</p>
            </div>
          )}

          {/* Double-tap like animation */}
          {liked && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Heart className="h-16 w-16 text-red-500 fill-red-500 animate-ping" />
            </div>
          )}
        </div>
        
        {/* Post Actions */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleLike}
                disabled={isLiking}
                className="focus:outline-none disabled:opacity-50 transition-transform active:scale-95"
              >
                <Heart 
                  className={`h-6 w-6 ${liked ? 'fill-red-500 text-red-500' : 'text-gray-700'} transition-all duration-200 ${liked ? 'scale-110' : ''}`}
                />
              </button>
              <button 
                onClick={() => setShowComments(true)}
                className="focus:outline-none transition-transform active:scale-95"
              >
                <MessageCircle className="h-6 w-6 text-gray-700" />
              </button>
              <button 
                onClick={handleShare}
                className="focus:outline-none transition-transform active:scale-95"
              >
                <Send className="h-6 w-6 text-gray-700" />
              </button>
            </div>
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="focus:outline-none disabled:opacity-50 transition-transform active:scale-95"
            >
              <Bookmark 
                className={`h-6 w-6 ${saved ? 'fill-current text-gray-700' : 'text-gray-700'}`} 
              />
            </button>
          </div>
          
          {/* Likes */}
          <p className="font-bold text-sm mb-1">
            {likes.toLocaleString()} {likes === 1 ? 'like' : 'likes'}
          </p>
          
          {/* Caption */}
          <p className="text-sm mb-1">
            <Link to={`/profile/${user.username}`} className="font-bold mr-1">
              {user.username}
            </Link>
            {renderCaption(caption)}
          </p>

          {/* Hashtags */}
          {hashtags && hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {hashtags.map((tag, index) => (
                <HashtagLink
                  key={index}
                  hashtag={tag.hashtag || tag}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                />
              ))}
            </div>
          )}
          
          {/* Comments */}
          <button 
            className="text-gray-500 text-sm mb-1 hover:text-gray-700 transition-colors"
            onClick={() => setShowComments(true)}
          >
            View all {comments.toLocaleString()} {comments === 1 ? 'comment' : 'comments'}
          </button>
          
          {/* Timestamp */}
          <p className="text-xs text-gray-500 uppercase mt-1">{timestamp}</p>
        </div>
        
        {/* Comment Input */}
        <div className="flex items-center px-3 py-2 border-t border-gray-200">
          <input
            type="text"
            placeholder="Add a comment..."
            className="flex-grow text-sm border-0 focus:ring-0 outline-none"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                setShowComments(true);
              }
            }}
          />
          <button 
            className="text-purple-600 font-semibold text-sm ml-2 hover:text-purple-700 transition-colors"
            onClick={() => setShowComments(true)}
          >
            Post
          </button>
        </div>
      </div>

      {/* Post Options Modal */}
      <PostOptionsModal
        isOpen={showOptionsModal}
        onClose={() => setShowOptionsModal(false)}
        isOwnPost={isOwnPost}
        onDelete={() => setShowDeleteModal(true)}
        onShare={handleShare}
        onCopyLink={handleCopyLink}
        onReport={!isOwnPost ? handleReport : undefined}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeletePost}
        isDeleting={isDeleting}
      />

      {/* Comments Modal */}
      <CommentsModal
        isOpen={showComments}
        onClose={() => setShowComments(false)}
        post={post}
        onCommentAdded={handleCommentAdded}
        onCommentDeleted={handleCommentDeleted}
      />
    </>
  );
};